// Section 17 step 4 and Section 22: the per-person and per-household override list. Pins are
// full / half / none (or cleared) and are honoured before the policy fills the rest. Warmth is
// shared by household, so its pins are per household and apply in Winter only. Props only.
import type { HouseholdId, JobId, PersonId } from '../../sim/api';
import {
  PIN_SHARES,
  type HouseholdRow,
  type PinRow,
  type RationTabResource,
} from '../../viewModels/rations';
import { rationsText } from '../text/en';

interface Props {
  resource: RationTabResource;
  people: PinRow[];
  households: HouseholdRow[];
  canEdit: boolean;
  jobLabel: (job: JobId) => string;
  onPin: (target: PersonId | HouseholdId, share: number | null) => void;
  onMyHousehold: (personId: PersonId, member: boolean) => void;
}

const LABEL: Record<(typeof PIN_SHARES)[number], string> = {
  1: rationsText.full,
  0.5: rationsText.half,
  0: rationsText.none,
};

function PinButtons({
  name,
  value,
  canEdit,
  onPin,
}: {
  name: string;
  value: number | null;
  canEdit: boolean;
  onPin: (share: number | null) => void;
}) {
  return (
    <span class="rations__pins" role="group" aria-label={`${rationsText.pin}: ${name}`}>
      {PIN_SHARES.map((share) => (
        <button
          key={share}
          type="button"
          class={`button button--small${value === share ? ' is-selected' : ''}`}
          aria-pressed={value === share}
          disabled={!canEdit}
          onClick={() => {
            onPin(value === share ? null : share);
          }}
        >
          {LABEL[share]}
        </button>
      ))}
      <button
        type="button"
        class="button button--small"
        disabled={!canEdit || value === null}
        onClick={() => {
          onPin(null);
        }}
      >
        {rationsText.clear}
      </button>
    </span>
  );
}

export function OverrideList({
  resource,
  people,
  households,
  canEdit,
  jobLabel,
  onPin,
  onMyHousehold,
}: Props) {
  const perPerson = resource !== 'warmth';
  return (
    <div class="rations__overrides">
      <p class="hint">
        {resource === 'warmth' ? rationsText.warmthPinHint : rationsText.overridesHint}
      </p>
      {perPerson && (
        <table class="table rations__table">
          <caption class="visually-hidden">{rationsText.peopleHeading}</caption>
          <thead>
            <tr>
              <th scope="col">{rationsText.person}</th>
              <th scope="col">{rationsText.age}</th>
              <th scope="col">{rationsText.job}</th>
              <th scope="col">{rationsText.pin}</th>
              {resource === 'food' && <th scope="col">{rationsText.myHousehold}</th>}
            </tr>
          </thead>
          <tbody>
            {people.map((p) => {
              const value = p[resource];
              return (
                <tr key={p.id} class={value === null ? '' : 'is-pinned'}>
                  <td class="name">
                    {p.name}
                    {value !== null && <span class="hint"> ({rationsText.pinned(value)})</span>}
                  </td>
                  <td class="num">{p.ageYears}</td>
                  <td>{jobLabel(p.job)}</td>
                  <td>
                    <PinButtons
                      name={p.name}
                      value={value}
                      canEdit={canEdit}
                      onPin={(share) => {
                        onPin(p.id, share);
                      }}
                    />
                  </td>
                  {resource === 'food' && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${rationsText.myHousehold}: ${p.name}`}
                        checked={p.inMyHousehold}
                        disabled={!canEdit}
                        onChange={(e) => {
                          onMyHousehold(p.id, e.currentTarget.checked);
                        }}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <table class="table rations__table">
        <caption class="visually-hidden">{rationsText.householdsHeading}</caption>
        <thead>
          <tr>
            <th scope="col">{rationsText.household}</th>
            <th scope="col">{rationsText.membersCol}</th>
            <th scope="col">{rationsText.pin}</th>
          </tr>
        </thead>
        <tbody>
          {households.map((h) => {
            const value = h[resource];
            const name = rationsText.householdName(h.name);
            return (
              <tr key={h.id} class={value === null ? '' : 'is-pinned'}>
                <td class="name">
                  {name}
                  {value !== null && <span class="hint"> ({rationsText.pinned(value)})</span>}
                </td>
                <td class="num">{h.members}</td>
                <td>
                  <PinButtons
                    name={name}
                    value={value}
                    canEdit={canEdit}
                    onPin={(share) => {
                      onPin(h.id, share);
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
